import { AsGrid, useModal } from '@adserve/adserve-react-components';
import React, { useState, useEffect, useContext } from 'react';
import QuotesGridColumnDefinitions from './GridColumnDefinitions';
import {
    DeleteQuote,
    UpdateQuote,
    GetReservationTimes,
    GetCampaignSettings,
    GetQuotePdf,
    GetAccountSettings
} from '../../../services/CampaignsDataService';
import { SystemSettingsContext } from '../../../contexts/SystemSettingsContext';
import { GetActiveRequest } from '../../../services/WorkflowService';
import { BusinessTypes } from '../../../constants/Enums';

const whiteButtonStyle = {
    border: '2px solid #CCCCCC',
    backgroundColor: '#FFFFFF',
    cursor: 'pointer'
};

const QuotesGrid = ({
    campaign,
    billingAccountCreditDetails,
    refreshCampaign,
    readOnly = false,
    setIsReserved,
    quotes,
    getQuotes,
    onNameChange,
    apiParamsDispatcher,
    isCampaignModified,
    updateCampaign,
    canApprove,
    campaignApprovalTeam,
    businessType,
    validateBooking,
    isCampaignBurst,
    activeRequest = {},
    discountRequest = {},
    dealDiscounts = [],
    onDiscountRequestApproval,
    isUsingDynamicWorkflow = false,
    discountApprovalWorkflowId,
    valuePotRequest,
    valuePotApprovalWorkflowId
}) => {
    const { dateFormat, timeFormat, locale, currency } = useContext(SystemSettingsContext);

    const [columnDefinitions, setColumnDefinitions] = useState([]);
    const [reservationTimeValues, setReservationTimeValues] = useState([]);
    const [scrolling, setScrolling] = useState(false);
    const [creditStatusWorkflowId, setCreditStatusWorkflowId] = useState();
    const [reservationWorkflowId, setReservationWorkflowId] = useState();
    const [preBookingWorkflowId, setPreBookingWorkflowId] = useState();
    const [bookingWorkflowId, setBookingWorkflowId] = useState();
    const [activeBookingRequest, setActiveBookingRequest] = useState();

    const { addMessage } = useModal();

    const refreshGrid = () => {
        setColumnDefinitions(
            QuotesGridColumnDefinitions(
                refreshCampaign,
                readOnly,
                getQuotes,
                reservationTimeValues,
                scrolling,
                creditStatusWorkflowId,
                reservationWorkflowId,
                preBookingWorkflowId,
                bookingWorkflowId,
                discountApprovalWorkflowId,
                isCampaignModified,
                updateCampaign,
                activeBookingRequest,
                updateActiveRequest,
                canApprove,
                campaignApprovalTeam,
                businessType,
                validateBooking,
                campaign,
                billingAccountCreditDetails,
                isCampaignBurst,
                activeRequest,
                discountRequest,
                dealDiscounts,
                onDiscountRequestApproval,
                isUsingDynamicWorkflow,
                valuePotRequest,
                valuePotApprovalWorkflowId
            )
        );
    };

    useEffect(() => {
        refreshGrid();
        if (scrolling) setScrolling(false);
    }, [readOnly, reservationTimeValues, preBookingWorkflowId, bookingWorkflowId, campaignApprovalTeam, isUsingDynamicWorkflow, scrolling === true]);

    useEffect(() => {
        if (activeBookingRequest) {
            refreshGrid();
        }
    }, [activeBookingRequest]);

    const handleScroll = () => {
        if (!scrolling) setScrolling(true);
    };

    useEffect(() => {
        GetReservationTimes().then((data) => {
            setReservationTimeValues(data);
        });

        GetAccountSettings().then((data) => {
            setCreditStatusWorkflowId(data.approvalWorkflowId);
        });

        GetCampaignSettings()
            .then((settings) => {
                setReservationWorkflowId(settings.reservationWorkflowId);

                if (businessType.toLowerCase() === BusinessTypes.SANDP.name.toLowerCase()) {
                    setPreBookingWorkflowId(settings.sandPPreBookingWorkflowId);
                    if (settings.sandPPreBookingWorkflowId) {
                        updateActiveRequest(settings.sandPPreBookingWorkflowId, campaign.id);
                    }

                    setBookingWorkflowId(settings.sandPPostBookingWorkflowId);
                    if (settings.sandPPostBookingWorkflowId) {
                        updateActiveRequest(settings.sandPPostBookingWorkflowId, campaign.id);
                    }
                } else {
                    setBookingWorkflowId(settings.preBookingWorkflowId);
                    if (settings.preBookingWorkflowId) {
                        updateActiveRequest(settings.preBookingWorkflowId, campaign.id);
                    }
                }
            })
            .catch(console.log);
    }, []);

    const updateActiveRequest = (workflowId, campaignId) => {
        GetActiveRequest(workflowId, campaignId).then((request) => {
            if (request) {
                setActiveBookingRequest(request);
            }
        });
    };

    const handleSortOrderChange = (column) => {
        const unchangedColumns = columnDefinitions
            .filter((c) => c.field !== column.field)
            .map((c) => ({ ...c, sortOrder: '' }));

        const sortField = column.field;

        setColumnDefinitions([...unchangedColumns, column]);
        apiParamsDispatcher({
            sortField: sortField,
            sortOrder: column.sortOrder,
            pageNumber: 1
        });
    };

    const handleDelete = async (quote) => {
        try {
            await DeleteQuote(quote.id);
            if (quote.quoteType === 1) {
                setIsReserved(false);
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`Falied to delete quote ${quote.id}.\n${err?.message || err}`);
            addMessage({
                message: `An error occured while deleting quote ${quote.name}.`,
                buttons: [
                    {
                        text: window.translate('Ok'),
                        callback: null
                    }
                ]
            });
        }
        refreshCampaign();
        await getQuotes();
    };

    const handleActionClick = (command, data) => {
        //if (readOnly || (data?.statusName === 'Booked' && !data.canDelete)) {
        if (readOnly || data?.statusName === 'Booked' || !data?.canDelete) { // ASSP-8148
            return;
        }

        switch (command) {
            case 'delete':
                addMessage({
                    message: window.translate(`Are you sure you want to delete quote ${data?.name}?`),
                    buttons: [
                        {
                            text: window.translate('No'),
                            style: { ...whiteButtonStyle, width: 87 },
                            callback: null
                        },
                        {
                            text: window.translate('Yes'),
                            style: { cursor: 'pointer' },
                            callback: () => handleDelete(data)
                        }
                    ]
                });
                break;
            default:
                break;
        }
    };

    /**
     * Gets the base64 generated quote from the database, then displays it in a new tab.
     * @param {Object} q - quote
     */
    const displayPdf = async (q) => {
        try {
            const base64Pdf = await GetQuotePdf(q.id);
            const newTab = window.open();
            newTab.document.title = q.name;
            newTab.document.body.style.margin = '0';
            newTab.document.body.innerHTML = `<iframe
                style="width: 100%;height: 100%;box-sizing: border-box;"
                src="data:application/pdf;base64,${base64Pdf}"></iframe>`;
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`Failed to open quote pdf${q.id}.\n${err?.message || err}`);
            addMessage({
                message: `An error occured while attempting to open Quote ${q.name}.`,
                buttons: [
                    {
                        text: window.translate('Ok'),
                        callback: null
                    }
                ]
            });
        }
    };

    const handleCustomClick = async (customType, q) => {
        switch (customType) {
            case 'pdf':
                await displayPdf(q);
                break;
        }
    };

    const handleIconDisabilty = (actionType, status, quote) => {
        return readOnly || status === 'Booked' || !quote.canDelete; // ASSP-8148
        //return readOnly || (status === 'Booked' && !quote.canDelete);
    };

    /**
     * key press event handler for quote name input boxes. Saves on enter keystroke
     * @param {Event} event - event
     * @param {Object} q - quote
     */
    const onKeyPress = async (event, q) => {
        if (event.key !== 'Enter') {
            return;
        }
        try {
            await UpdateQuote(q);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`Failed to update quote ${q.id}.\n${err?.message || err}`);
            addMessage({
                message: `An error occured while updating quote ${q?.name}.`,
                buttons: [
                    {
                        text: window.translate('Ok'),
                        callback: null
                    }
                ]
            });
        }
        await getQuotes();
    };

    return (
        <>
            <AsGrid
                columnDefs={columnDefinitions}
                gridContainerStyle={{ gridTemplateRows: '8px 1fr 2px 2px' }}
                pageInfo={{
                    count: 5,
                    hasNextPage: false,
                    hasPrevPage: false,
                    isFirstPage: true,
                    isLastPage: true,
                    pageCount: 1,
                    pageNumber: 1,
                    pageSize: -1,
                    totalItemCount: 3,
                    pageSizeOptions: [{ value: -1, text: 'All' }]
                }}
                data={quotes}
                style={{ borderLeft: '1px solid #D7D7D7' }}
                headerRowHeight={44}
                rowHeight={56}
                maxVisibleRows={8}
                onSortOrderChange={handleSortOrderChange}
                onActionClick={handleActionClick}
                onCustomClick={handleCustomClick}
                onActionIconLoad={handleIconDisabilty}
                onScroll={handleScroll}
                dateFormat={dateFormat}
                timeFormat={timeFormat}
                locale={locale}
                currency={currency}
                onChange={onNameChange}
                onKeyPress={onKeyPress}
                showColumnFilter={false}
            />
        </>
    );
};

export default QuotesGrid;
